// Atur Pengiriman Massal Shopee — Gudang Rido (Periksa → Jadwal & Konfirmasi → Resi & Cetak Berpasangan).
// Aksi tulis ke Shopee ditolak server bila saklar belum diaktifkan Owner.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MS = `${API}/shopee/mass-ship`;
const opt = { withCredentials: true };
export const errMsg = (e, d) => {
  const x = e?.response?.data?.detail ?? e?.message ?? d;
  if (typeof x === "string") return x || d;
  if (Array.isArray(x)) return x.map(i => (i && i.msg) || String(i)).join("; ") || d;
  return d;
};

class PanelGuard extends React.Component {
  constructor(p) { super(p); this.state = { broken: false }; }
  static getDerivedStateFromError() { return { broken: true }; }
  render() {
    if (!this.state.broken) return this.props.children;
    return createPortal(<div className="modal-backdrop" style={{ zIndex: 1100 }}><div className="modal" style={{ maxWidth: 480 }} data-testid="mass-ship-crash">
      <p>Terjadi gangguan tampilan. Tidak ada pesanan yang dikirim ulang. Tutup lalu buka lagi; jika berulang, hubungi Owner.</p>
      <button type="button" className="primary" onClick={this.props.onClose} data-testid="mass-ship-crash-close">Tutup</button></div></div>, document.body);
  }
}
export function useMassShipStatus() {
  const [st, setSt] = useState(null);
  useEffect(() => { axios.get(`${MS}/status`, opt).then(r => setSt(r.data?.visible ? r.data : null)).catch(() => setSt(null)); }, []);
  return st;
}

function LatestRun() {
  const [run, setRun] = useState(null);
  useEffect(() => { axios.get(`${MS}/runs/latest`, opt).then(r => { const x = r.data.run;
    if (x && (x.status === "running" || Date.now() - new Date(x.finished_at || x.created_at).getTime() < RECENT_MS)) setRun(x); }).catch(() => {}); }, []);
  return run ? <RunProgress run={run} onUpdate={setRun} onDismiss={() => setRun(null)}/> : null;
}

export function MassShipSection({ count, status, onOpen }) {
  const locked = !status.can_open;
  const tag = locked ? " — Belum Diaktifkan" : "";
  return <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #e3d6b0" }} data-testid="wh-shopee-mass-ship-section">
    <p className="small" style={{ fontWeight: 700, margin: "0 0 2px" }} data-testid="wh-shopee-batch-opt2-title">Pilihan 2 — Cetak Mockup + Resi</p>
    <p className="muted small" style={{ marginTop: 0 }}>Kertas A5 Portrait (148 × 210 mm, tegak) · mockup otomatis diputar 90° · resi tetap tegak · urutan Mockup → Resi per pesanan.</p>
    {status.switch_on && <LatestRun/>}
    {status.pilot && status.write_enabled && <div className="warn-box small" data-testid="wh-shopee-mass-ship-pilot">
      Uji coba: hanya pesanan {status.pilot_orders?.length ? status.pilot_orders.join(", ") : "(menunggu nomor dari Owner)"} yang boleh diproses · maks {status.max_per_run} pesanan per proses.</div>}
    {!status.write_enabled && <div className="warn-box small" data-testid="wh-shopee-mass-ship-locked">{status.killed && status.switch_on ? "Dimatikan sementara oleh Owner" : "Menunggu aktivasi Owner"}</div>}
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
      <button type="button" className="soft-btn" disabled={locked || count === 0} onClick={() => onOpen(2)}
        data-testid="wh-shopee-mass-ship-print">Ambil Resi & Cetak (A5 Portrait){tag}</button>
    </div>
  </div>;
}

const STEPS = ["Periksa Pesanan", "Atur Pengiriman", "Ambil Resi & Cetak"];
const AUTO_DELAYS = [3, 10, 15, 20, 30, 45, 60];   // detik; jeda bertahap, maks 1×/menit (aman utk batas API Shopee)
const AUTO_MAX_MS = 30 * 60 * 1000;
const AUTO_DOC_TRIES = 2;   // dokumen resi yang gagal tidak dibuat ulang terus-menerus

const b64Url = (b64) => {
  const bin = atob(b64); const a = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([a], { type: "application/pdf" }));
};

const RUN_STATE = { processing: "Diproses", ok: "Berhasil", failed: "Gagal", check: "Perlu Pemeriksaan", skipped: "Dilewati" };
const RUN_KIND = { arrange: "Atur Pengiriman", documents: "Ambil Resi" };
const RECENT_MS = 12 * 3600 * 1000;
const ACT_CHECK = "Jangan atur ulang di Seller Centre — tekan Coba Lagi di langkah Ambil Resi & Cetak untuk memeriksa.";

// Normalisasi bentuk run dari server (lama/baru) agar tampilan tidak pernah crash.
const ZERO = { processing: 0, ok: 0, failed: 0, check: 0, skipped: 0 };
export function normRun(r) {
  if (!r || !r.id) return null;
  const items = Array.isArray(r.items) ? r.items : Object.entries(r.items || {}).map(([job_id, it]) => ({ job_id, ...it }));
  const counts = { ...ZERO, ...(r.counts || {}) };
  if (!r.counts) items.forEach(it => { if (it.state in counts) counts[it.state] += 1; });
  return { ...r, items, counts };
}
const forBatch = (r, ids) => !!r && r.items.some(it => ids.includes(it.job_id));

// Ringkasan proses: 1 kalimat utama; angka teknis & daftar per pesanan di detail yang bisa dibuka.
export function RunProgress({ run, onUpdate, onDismiss }) {
  useEffect(() => {
    if (run.status !== "running") return undefined;
    const t = setInterval(() => axios.get(`${MS}/runs/${run.id}`, opt).then(r => { if (r.data?.run) onUpdate(r.data.run); }).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, [run.id, run.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const c = run.counts, kind = RUN_KIND[run.kind] || "Proses";
  const head = run.status === "running" ? `${kind} sedang diproses… (${c.ok + c.failed + c.check + c.skipped}/${run.items.length}) — halaman boleh ditutup.`
    : run.kind === "arrange" ? (c.ok ? `Pengiriman berhasil diatur: ${c.ok} pesanan.` : "Belum ada pengiriman yang berhasil diatur.")
    : (c.ok ? `Resi berhasil diambil: ${c.ok} pesanan.` : "Belum ada resi yang berhasil diambil.");
  const probs = run.items.filter(it => it.state === "failed" || it.state === "check");
  return <div className="warn-box small" style={{ marginBottom: 10 }} data-testid="mass-ship-run">
    <div style={{ fontWeight: 700 }} data-testid="mass-ship-run-head">{head}</div>
    {run.status === "stopped" && <div>Dihentikan Owner. Yang sudah diterima Shopee tetap tercatat.</div>}
    {probs.length > 0 && run.status !== "running" && <div style={{ marginTop: 4 }} data-testid="mass-ship-run-problems">
      {probs.map(it => <div key={it.job_id}><b>{it.order_number}</b> — {it.state === "check" ? `Perlu Pemeriksaan. ${ACT_CHECK}` : `Gagal: ${it.reason || "ditolak Shopee"}`}</div>)}
    </div>}
    <details style={{ marginTop: 4 }} data-testid="mass-ship-run-details">
      <summary>Lihat detail</summary>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "4px 0" }} data-testid="mass-ship-run-counts">
        <span>Diproses: {c.processing}</span><span>Berhasil: {c.ok}</span><span>Gagal: {c.failed}</span>
        <span>Perlu Pemeriksaan: {c.check}</span><span>Dilewati: {c.skipped}</span>
      </div>
      <div style={{ maxHeight: 140, overflow: "auto" }}>
        {run.items.map(it => <div key={it.job_id} data-testid={`mass-ship-run-item-${it.job_id}`}>
          <b>{it.order_number}</b> — {RUN_STATE[it.state] || it.state}{it.reason ? ` · ${it.reason}` : ""}</div>)}
      </div>
    </details>
    {run.status !== "running" && onDismiss && <button type="button" className="link-btn small" onClick={onDismiss} data-testid="mass-ship-run-dismiss">Sembunyikan</button>}
  </div>;
}

// Progres singkat (bukan tiga kotak); ✓ hanya bila langkah benar-benar selesai.
function StepBar({ step, done, onPick }) {
  return <div className="small msx-steps" data-testid="mass-ship-steps">
    <b data-testid="mass-ship-progress">Langkah {step + 1}/3: {STEPS[step]}{done[step] ? " ✓" : ""}</b>
    <span className="muted"> · {STEPS.map((s, i) => { const st = done[i] ? "done" : i === step ? "active" : "todo";
      return <React.Fragment key={s}>{i > 0 && " › "}<button type="button" className="link-btn small" onClick={() => onPick(i)}
        data-testid={`mass-ship-step-${i + 1}`} data-state={st} aria-current={i === step ? "step" : undefined}
        style={{ fontWeight: i === step ? 700 : 400 }}>{st === "done" ? "✓ " : ""}{s}{i === 1 && !done[1] && step > 1 ? " (belum selesai)" : ""}</button></React.Fragment>; })}</span>
  </div>;
}

function OwnerKill({ status, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (busy) return;
    const on = !status.killed;
    if (on && !window.confirm("Matikan Darurat: semua pengiriman ke Shopee langsung dikunci. Lanjutkan?")) return;
    setBusy(true);
    try { await axios.post(`${MS}/kill?on=${on}`, {}, opt); toast.success(on ? "Pengiriman massal dikunci." : "Kunci darurat dibuka."); onChange(); }
    catch (e) { toast.error(errMsg(e, "Gagal mengubah kunci.")); }
    finally { setBusy(false); }
  };
  return <button type="button" className="soft-btn small-btn" style={status.killed ? undefined : { borderColor: "#b3261e", color: "#b3261e", fontWeight: 700 }} disabled={busy} onClick={toggle}
    data-testid="mass-ship-kill">{status.killed ? "Buka Kunci Darurat" : "Matikan Darurat"}</button>;
}

export function MassShipPanel(props) {
  return <PanelGuard onClose={props.onClose}><MassShipPanelInner {...props}/></PanelGuard>;
}

function MassShipPanelInner({ jobs, ensureProcessing, onDone, onClose, initialStep = 0, status: status0 }) {
  const ids = jobs.map(j => j.id);
  const [step, setStep] = useState(initialStep);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");
  const [run, setRunRaw] = useState(null);
  // hanya run milik batch ini yang ditampilkan (hasil batch lain tidak terbawa)
  const setRun = (r) => { const x = normRun(r); setRunRaw(x && forBatch(x, ids) ? x : null); };
  const [status, setStatus] = useState(status0 || null);
  const load = () => { setErr(""); setPlan(null);
    axios.post(`${MS}/prepare`, { job_ids: ids }, opt).then(r => setPlan(r.data)).catch(e => setErr(errMsg(e, "Gagal memeriksa pesanan."))); };
  const loadStatus = () => axios.get(`${MS}/status`, opt).then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadStatus();
    axios.get(`${MS}/runs/latest`, opt).then(r => { const x = normRun(r.data?.run);
      if (x && (x.status === "running" || Date.now() - new Date(x.finished_at || x.created_at).getTime() < RECENT_MS)) setRun(x); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const running = run && run.status === "running";
  const lastArr = useRef("");
  useEffect(() => {   // pengiriman selesai & ada yang berhasil → otomatis ke langkah 3
    if (run && run.kind === "arrange" && run.status !== "running" && run.counts.ok > 0 && lastArr.current !== run.id) {
      lastArr.current = run.id; if (step === 1) { setStep(2); load(); }
    }
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps
  const arrangedAll = !!plan && plan.groups.length === 0 && plan.counts.already > 0;
  const arrangedRun = !!run && run.kind === "arrange" && run.status !== "running" && run.counts.ok > 0;
  const done = [step > 0, arrangedAll || arrangedRun, false];   // "Selesai" hanya bila Shopee benar-benar menerima
  return createPortal(<div className="modal-backdrop" style={{ zIndex: 1100 }}>
    <div className="modal" style={{ maxWidth: 760, width: "96vw" }} data-testid="mass-ship-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <h3 style={{ margin: 0 }}>Atur Pengiriman · {jobs.length} pesanan</h3>
        <div style={{ display: "flex", gap: 6 }}>
          {status?.is_owner && <OwnerKill status={status} onChange={() => { loadStatus(); load(); }}/>}
          <button type="button" className="soft-btn small-btn" onClick={onClose} data-testid="mass-ship-close">Tutup</button>
        </div>
      </div>
      {plan && !plan.write_enabled && <div className="warn-box small" style={{ marginTop: 8 }} data-testid="mass-ship-disabled">
        {status?.killed ? "Dimatikan sementara oleh Owner." : "Belum Diaktifkan — menunggu aktivasi Owner."} Pemeriksaan tetap bisa dilakukan.</div>}
      <StepBar step={step} done={done} onPick={setStep}/>
      {run && <RunProgress run={run} onUpdate={setRun} onDismiss={() => setRun(null)}/>}
      {err && <div className="warn-box small" data-testid="mass-ship-error">{err}</div>}
      {!plan && !err && <p className="muted small" data-testid="mass-ship-loading">Memeriksa pesanan ke Shopee…</p>}
      {plan && step === 0 && <CheckStep plan={plan} onNext={() => setStep(plan.groups.length ? 1 : 2)} onReload={load}/>}
      {plan && step === 1 && <ScheduleStep plan={plan} busyRun={running} onStarted={setRun}/>}
      {plan && step === 2 && <AwbStep jobs={jobs} writeEnabled={plan.write_enabled} busyRun={running} onStarted={setRun}
        runTick={run && run.status !== "running" ? run.finished_at : ""} ensureProcessing={ensureProcessing} onDone={onDone}/>}
    </div>
  </div>, document.body);
}

// Alasan → tindakan operator (bahasa sederhana).
export const actionFor = (r) => r.state === "fast" ? "Proses manual di Seller Centre Shopee saat barang siap."
  : r.state === "unverified" ? "Tekan Periksa Ulang beberapa saat lagi. Bila tetap, hubungi Owner."
  : "Proses manual di Seller Centre Shopee, atau tanyakan ke Owner.";

function CheckStep({ plan, onNext, onReload }) {
  const c = plan.counts;
  const ready = plan.groups.reduce((n, g) => n + g.job_ids.length, 0);
  const bad = plan.rows.filter(r => !r.eligible && r.state !== "already");
  return <div data-testid="mass-ship-check">
    <div style={{ fontSize: 15, fontWeight: 700 }} data-testid="mass-ship-check-head">
      {ready ? `${ready} pesanan siap diatur pengirimannya (Drop-off).` : c.already ? "Semua pesanan siap sudah diatur pengirimannya." : "Belum ada pesanan yang bisa diatur pengirimannya."}</div>
    {c.already > 0 && <div className="small" style={{ color: "#1f7a3a", fontWeight: 600 }} data-testid="mass-ship-already">✓ Pengiriman berhasil diatur: {c.already} pesanan</div>}
    {bad.length > 0 && <div className="warn-box small" style={{ marginTop: 8 }} data-testid="mass-ship-skipped-list">
      <div style={{ fontWeight: 600 }} data-testid="mass-ship-skip-count">{bad.length} pesanan perlu ditangani manual (mockup tetap bisa dicetak):</div>
      {bad.map(r => <div key={r.job_id} style={{ marginTop: 4 }} data-testid={`mass-ship-skip-${r.job_id}`}><b>{r.order_number}</b> — {r.reason}
        <div className="muted">Tindakan: {actionFor(r)}</div></div>)}
    </div>}
    <details style={{ marginTop: 8 }} className="small" data-testid="mass-ship-check-details">
      <summary>Lihat detail</summary>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }} data-testid="mass-ship-counts">
        <span>Dipilih: {c.selected}</span><span>Memenuhi syarat: {c.eligible}</span><span>Sudah diatur: {c.already}</span>
        <span>Belum dapat diverifikasi: {c.unverified}</span><span>Pengiriman cepat: {c.fast || 0}</span><span>Tidak memenuhi syarat: {c.blocked}</span>
      </div>
      {plan.groups.map(g => <div key={g.key} data-testid={`mass-ship-group-${g.key}`}>
        <b>{g.carrier}</b> · {g.job_ids.length} pesanan · Drop-off{g.options.dropoff_needs_branch ? " (pilih lokasi drop-off)" : ""}</div>)}
    </details>
    <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
      {(ready > 0 || c.already > 0) && <button type="button" className="primary" onClick={onNext} data-testid="mass-ship-next">
        {ready ? "Lanjut: Atur Pengiriman" : "Lanjut: Ambil Resi & Cetak"}</button>}
      <button type="button" className="link-btn small" onClick={onReload} data-testid="mass-ship-recheck">Periksa Ulang</button>
    </div>
  </div>;
}

function GroupChoice({ g, value, onChange }) {
  const o = g.options, v = value || {};
  return <div className="small" style={{ border: "1px solid #e3d6b0", borderRadius: 8, padding: 10, marginBottom: 8 }} data-testid={`mass-ship-choice-${g.key}`}>
    <div style={{ fontWeight: 600 }}>{g.carrier} · {g.job_ids.length} pesanan {g.shop_name ? `· ${g.shop_name}` : ""}</div>
    <div style={{ margin: "6px 0" }} data-testid={`mass-ship-method-dropoff-${g.key}`}>Metode: <b>Drop-off (antar ke agen)</b> · kurir & layanan sesuai pilihan pelanggan</div>
    {o.dropoff_needs_branch && <select value={v.branch_id || ""} onChange={e => onChange({ method: "dropoff", branch_id: e.target.value })}
      data-testid={`mass-ship-branch-${g.key}`}>
      <option value="">Pilih lokasi drop-off…</option>
      {o.branches.map(b => <option key={b.branch_id} value={b.branch_id}>{b.label || b.branch_id}</option>)}
    </select>}
  </div>;
}

export const choiceComplete = (g, v) => !!v && v.method === "dropoff" && g.options.methods.includes("dropoff")
  && (!g.options.dropoff_needs_branch || !!v.branch_id);

const defaultChoices = (groups) => Object.fromEntries(groups.filter(g => g.options.methods.includes("dropoff") && !g.options.dropoff_needs_branch)
  .map(g => [g.key, { method: "dropoff" }]));

function ScheduleStep({ plan, busyRun, onStarted }) {
  const [choices, setChoices] = useState(() => defaultChoices(plan.groups));
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);   // cegah klik ganda sebelum render ulang
  const ready = plan.groups.filter(g => choiceComplete(g, choices[g.key]));
  const n = ready.reduce((s, g) => s + g.job_ids.length, 0);
  const max = plan.max_per_run || 1;
  const send = async () => {
    if (lock.current || busyRun) return;
    lock.current = true; setBusy(true);
    try {
      const body = { job_ids: ready.flatMap(g => g.job_ids), confirm: true, choices: Object.fromEntries(ready.map(g => [g.key, choices[g.key]])) };
      const r = await axios.post(`${MS}/execute`, body, opt);
      onStarted(r.data);
      toast.success("Atur Pengiriman sedang diproses. Halaman boleh ditutup.");
    } catch (e) { toast.error(errMsg(e, "Gagal mengirim Atur Pengiriman.")); }
    finally { lock.current = false; setBusy(false); }
  };
  if (!plan.groups.length) return <p className="muted small" data-testid="mass-ship-no-groups">Tidak ada pesanan yang memenuhi syarat untuk diatur.</p>;
  return <div data-testid="mass-ship-schedule">
    {plan.groups.map(g => <GroupChoice key={g.key} g={g} value={choices[g.key]} onChange={v => setChoices(c => ({ ...c, [g.key]: v }))}/>)}
    <label className="small" style={{ display: "flex", gap: 6, alignItems: "flex-start", margin: "8px 0" }}>
      <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} data-testid="mass-ship-agree"/>
      Saya mengerti: {n} pesanan akan dikirim ke Shopee sebagai "Atur Pengiriman" dengan Drop-off, dan tidak dapat dibatalkan dari TAMFARIS.
    </label>
    {n > max && <div className="warn-box small" data-testid="mass-ship-over-max">Maksimal {max} pesanan per proses — kurangi pilihan pesanan.</div>}
    <button type="button" className="primary" disabled={!plan.write_enabled || !agree || n === 0 || n > max || busy || busyRun}
      onClick={send} data-testid="mass-ship-send">
      {!plan.write_enabled ? "Belum Diaktifkan" : busyRun ? "Proses lain sedang berjalan…" : busy ? "Mengirim ke Shopee…" : `Atur Pengiriman (${n} Pesanan)`}</button>
    {ready.length < plan.groups.length && <span className="muted small" style={{ marginLeft: 8 }} data-testid="mass-ship-unpicked">
      {plan.groups.length - ready.length} kelompok belum dipilih lokasi drop-off-nya (tidak ikut dikirim)</span>}
  </div>;
}

// Satu status sederhana per pesanan.
export function plainStatus(a, isFast, fastReason) {
  if (isFast) return { text: fastReason, act: "Proses manual di Seller Centre Shopee saat barang siap." };
  if (a.ship_state === "failed") return { text: "Gagal diatur", act: a.ship_error ? `${a.ship_error}` : "Proses manual di Seller Centre Shopee." };
  if (a.ship_state === "uncertain" || a.ship_state === "arranging") return { text: "Perlu Pemeriksaan", act: ACT_CHECK };
  if (a.ship_state !== "arranged") return { text: "Belum diatur", act: "" };
  if (a.tracking_state !== "available" && a.tracking_error) return { text: "Resi gagal diambil", act: `${a.tracking_error} — tekan Coba Lagi.`, wait: true };
  if (a.tracking_state !== "available") return { text: "Menunggu Resi Shopee", act: "", wait: true };
  if (a.doc_state === "ready") return { text: a.awb_printed_at ? "Resi sudah dicetak" : "Resi siap dicetak", act: "" };
  if (a.doc_state === "failed") return { text: "Dokumen resi gagal dibuat", act: a.doc_error || "Tekan Ambil Resi & Cetak untuk mencoba lagi." };
  return { text: "Pengiriman berhasil diatur", act: "" };
}

async function waitRun(id, onUpdate, alive, ms = 180000) {
  const end = Date.now() + ms;
  while (Date.now() < end && alive.current) {
    await new Promise(r => setTimeout(r, 2000));
    try { const x = (await axios.get(`${MS}/runs/${id}`, opt)).data?.run; if (!x || !alive.current) continue;
      onUpdate(x); if (x.status !== "running") return x; } catch {}
  }
  return null;
}

function AwbStep({ jobs, writeEnabled, busyRun, onStarted, runTick, ensureProcessing, onDone }) {
  const ids = jobs.map(j => j.id);
  const [items, setItems] = useState(null);
  const [fast, setFast] = useState({ ids: [], reason: "" });
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [printRes, setPrintRes] = useState(null);
  const [reprint, setReprint] = useState(false);
  const lock = useRef(false), alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const by = Object.fromEntries((items || []).map(a => [a.job_id, a]));
  const call = async (url, msg) => {
    try { const r = await axios.post(url, { job_ids: ids }, opt); if (!alive.current) return null;
      if (Array.isArray(r.data?.items) && !r.data?.kind) setItems(r.data.items);   // hasil proses (run) bukan status resi
      if (Array.isArray(r.data?.fast)) setFast({ ids: r.data.fast, reason: r.data.fast_reason || "" }); return r.data || {}; }
    catch (e) { if (msg && alive.current) toast.error(errMsg(e, msg)); return null; }
  };
  const once = async (key, fn) => {
    if (lock.current) return;
    lock.current = true; setBusy(key); setNote("");
    try { await fn(); } catch (e) { if (alive.current) setNote(errMsg(e, "Gagal mengambil resi. Coba lagi.")); }
    finally { lock.current = false; if (alive.current) setBusy(""); }
  };
  useEffect(() => { call(`${MS}/state`, "Gagal memuat status resi."); }, [runTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const st = jobs.map(j => ({ j, s: plainStatus(by[j.id] || {}, fast.ids.includes(j.id), fast.reason) }));
  const waiting = st.filter(x => x.s.wait).length;
  // Satu tombol: perbarui resi → ambil dokumen yang belum siap (latar belakang) → PDF berpasangan → konfirmasi cetak.
  const go = () => once("go", async () => {
    const t = await call(`${MS}/tracking`, "Gagal memperbarui resi.");
    if (!t) return;
    const need = (t.items || []).filter(a => a.tracking_state === "available" && a.doc_state !== "ready" && !fast.ids.includes(a.job_id));
    if (need.length && writeEnabled && !busyRun) {
      const t0 = Date.now();
      setNote(`Mengambil resi dari Shopee… 0/${need.length} pesanan`);
      const upd = (x) => { onStarted(x); const r = normRun(x); if (!r || !alive.current) return;
        const c = r.counts; setNote(`Mengambil resi dari Shopee… ${c.ok + c.failed + c.check + c.skipped}/${r.items.length} pesanan · ${Math.round((Date.now() - t0) / 1000)} detik`); };
      const d = await call(`${MS}/documents`, "Gagal mengambil resi.");
      if (d && d.id) { upd(d); await waitRun(d.id, upd, alive); if (!alive.current) return; }
    }
    const ok = (await call(`${MS}/state`, "")) || {};
    const all = ok.items || [];
    const done = new Set(all.filter(a => a.awb_printed_at).map(a => a.job_id));
    const pdfIds = reprint ? ids : ids.filter(id => !done.has(id));   // yang sudah dicetak tidak ikut lagi (tanpa duplikasi)
    const fails = all.filter(a => pdfIds.includes(a.job_id) && a.ship_state === "arranged" && a.doc_state !== "ready" && !fast.ids.includes(a.job_id));
    const failText = fails.map(a => `${a.order_number || a.order_sn}: ${a.doc_error || a.tracking_error || "Menunggu Resi Shopee"}`).join("; ");
    if (!pdfIds.length) { setNote("Semua resi pesanan ini sudah dicetak — centang \"Cetak ulang yang sudah dicetak\" bila perlu mencetak lagi."); return; }
    if (!all.some(a => pdfIds.includes(a.job_id) && a.doc_state === "ready")) { setNote(`Resi belum terambil — ${failText || "Menunggu Resi Shopee"}. Tekan Coba Lagi beberapa menit lagi.`); return; }
    setNote("Menyiapkan PDF…");
    let p = null;
    try { p = (await axios.post(`${MS}/paired-pdf`, { job_ids: pdfIds }, opt)).data; }
    catch (e) { setNote(errMsg(e, "Gagal menyiapkan PDF.")); return; }
    if (!alive.current || !p?.pdf_base64) { if (alive.current) setNote("PDF tidak diterima dari server. Coba lagi."); return; }
    const url = b64Url(p.pdf_base64);
    const w = window.open(url, "_blank");
    if (w) w.opener = null; else toast.warning("Browser memblokir tab PDF — buka \"Opsi tambahan\" lalu tekan \"Buka PDF\".");
    const skipDone = ids.length - pdfIds.length;
    setNote([fails.length ? `Tidak ikut PDF (resi belum terambil): ${failText}` : "", skipDone ? `${skipDone} pesanan sudah dicetak sebelumnya — tidak dicetak ulang.` : ""].filter(Boolean).join(" · "));
    setPrintRes({ ...p, url });
  });
  const retry = () => once("retry", async () => { auto.current.start = Date.now(); auto.current.tries = 0; await call(`${MS}/tracking`, "Gagal memperbarui resi."); });
  // Cek otomatis: resi yang menunggu dibaca ulang (baca-saja) dengan jeda bertahap; resi yang sudah ada langsung diambil dokumennya.
  const auto = useRef({ start: Date.now(), tries: 0, docTry: {}, busy: false });
  const [autoTick, setAutoTick] = useState(0);
  const [autoInfo, setAutoInfo] = useState(null);
  const docIds = (items || []).filter(a => ids.includes(a.job_id) && a.tracking_state === "available" && a.doc_state !== "ready"
    && !fast.ids.includes(a.job_id) && (auto.current.docTry[a.job_id] || 0) < AUTO_DOC_TRIES).map(a => a.job_id);
  const want = !!items && (waiting > 0 || (writeEnabled && docIds.length > 0));
  useEffect(() => {
    if (!want || busyRun || busy) { setAutoInfo(null); return undefined; }
    const a = auto.current;
    if (Date.now() - a.start > AUTO_MAX_MS) { setAutoInfo({ stopped: true }); return undefined; }
    const sec = AUTO_DELAYS[Math.min(a.tries, AUTO_DELAYS.length - 1)];
    setAutoInfo({ sec, tries: a.tries });
    const t = setTimeout(async () => {
      if (a.busy || !alive.current) return;
      a.busy = true; a.tries += 1;
      try {
        const cur = waiting > 0 ? await call(`${MS}/tracking`, "") : { items };
        const docs = ((cur || {}).items || []).filter(x => ids.includes(x.job_id) && x.tracking_state === "available" && x.doc_state !== "ready"
          && !fast.ids.includes(x.job_id) && (a.docTry[x.job_id] || 0) < AUTO_DOC_TRIES);
        if (docs.length && writeEnabled && alive.current) {
          docs.forEach(x => { a.docTry[x.job_id] = (a.docTry[x.job_id] || 0) + 1; });
          const d = await call(`${MS}/documents`, "");
          if (d && d.id) { onStarted(d); await waitRun(d.id, onStarted, alive); if (alive.current) await call(`${MS}/state`, ""); }
        }
      } finally { a.busy = false; if (alive.current) setAutoTick(x => x + 1); }
    }, sec * 1000);
    return () => clearTimeout(t);
  }, [want, busyRun, busy, autoTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const ready = st.filter(({ j }) => by[j.id]?.tracking_state === "available" && !fast.ids.includes(j.id)).length;
  const goLabel = ready && !waiting ? `Cetak Massal (${ready})` : ready ? `Cetak yang Siap (${ready})` : "Ambil Resi & Cetak";
  const probs = st.filter(({ s }) => (!s.wait && !!s.act) || s.text === "Belum diatur");
  return <div className="msx" data-testid="mass-ship-awb">
    <div className="msx-chips" data-testid="mass-ship-awb-summary">
      <span className="msx-chip" data-testid="mass-ship-count-selected"><b>{jobs.length}</b> Dipilih</span>
      <span className="msx-chip is-ok" data-testid="mass-ship-count-ready"><b>{ready}</b> Siap Cetak</span>
      <span className={`msx-chip${waiting ? " is-wait" : ""}`} data-testid="mass-ship-count-waiting"><b>{waiting}</b> Menunggu Resi</span>
    </div>
    {waiting > 0 && <div className="small msx-line" data-testid="mass-ship-waiting">
      Menunggu Resi Shopee: {waiting} pesanan. <button type="button" className="link-btn small" disabled={!!busy} onClick={retry}
        data-testid="mass-ship-retry-tracking">{busy === "retry" ? "Memeriksa…" : "Coba Lagi"}</button></div>}
    {autoInfo && <div className="small muted msx-line" data-testid="mass-ship-auto">{autoInfo.stopped
      ? "Pengecekan otomatis berhenti setelah 30 menit — tekan Coba Lagi untuk melanjutkan."
      : `Cek otomatis aktif · berikutnya dalam ${autoInfo.sec} detik${autoInfo.tries ? ` (percobaan ke-${autoInfo.tries + 1})` : ""} · tidak perlu menekan Coba Lagi.`}</div>}
    {probs.length > 0 && <div className="warn-box small msx-probs" data-testid="mass-ship-awb-problems">
      <b>{probs.length} pesanan belum bisa dicetak resinya:</b>
      {probs.map(({ j, s }) => <div key={j.id} data-testid={`mass-ship-problem-${j.id}`}><b>{j.order_number}</b> — {s.text}{s.act ? `. ${s.act}` : ""}</div>)}
    </div>}
    {note && <div className="small msx-line" style={{ fontWeight: 600 }} data-testid="mass-ship-awb-note">{note}</div>}
    <div className="msx-actions">
      <button type="button" className="primary msx-main" disabled={!!busy || busyRun} onClick={go} data-testid="mass-ship-print-paired">
        {busy === "go" ? "Memproses…" : busyRun ? "Mengambil resi otomatis…" : goLabel}</button>
      {st.some(({ j }) => by[j.id]?.awb_printed_at) && <label className="small msx-reprint">
        <input type="checkbox" checked={reprint} disabled={!!busy} onChange={e => setReprint(e.target.checked)} data-testid="mass-ship-reprint"/> Cetak ulang yang sudah dicetak</label>}
    </div>
    <p className="muted small msx-line" data-testid="mass-ship-pickup-reminder">
      Ambil resi SEBELUM paket diserahkan ke agen drop-off — setelah diserahkan, Shopee tidak bisa membuat resi lagi.</p>
    <details className="small msx-details" data-testid="mass-ship-awb-details">
      <summary>Lihat Detail ({jobs.length} pesanan)</summary>
      <div className="msx-table-wrap">
        <table className="msx-table" data-testid="mass-ship-awb-table">
          <thead><tr><th>Pesanan</th><th>Status</th><th>Resi</th><th>Kode Booking</th><th>Dicetak</th></tr></thead>
          <tbody>{st.map(({ j, s }) => { const a = by[j.id] || {}; return <tr key={j.id} data-testid={`mass-ship-row-${j.id}`}>
            <td><b>{j.order_number}</b></td>
            <td data-testid={`mass-ship-ship-${j.id}`}>{s.text}{s.act && <div className="muted" data-testid={`mass-ship-act-${j.id}`}>Tindakan: {s.act}</div>}</td>
            <td data-testid={`mass-ship-tracking-${j.id}`}>{a.tracking_state === "available" ? a.tracking_number : "—"}</td>
            <td>{a.booking_code || "—"}</td>
            <td>Mockup {j.mockup_printed_at ? "✓" : "—"} · Resi {a.awb_printed_at ? "✓" : "—"}</td>
          </tr>; })}</tbody>
        </table>
      </div>
    </details>
    {printRes && <PrintConfirm res={{ orders: [], skipped: [], no_awb: [], pages: [], ...printRes }} jobs={jobs} ensureProcessing={ensureProcessing}
      onClose={async (changed) => { URL.revokeObjectURL(printRes.url); setPrintRes(null); if (changed) { await onDone(); call(`${MS}/state`, ""); } }}/>}
  </div>;
}

function PrintConfirm({ res, jobs, ensureProcessing, onClose }) {
  const byId = Object.fromEntries(jobs.map(j => [j.id, j]));
  const [sel, setSel] = useState(() => Object.fromEntries(res.orders.map(o => [o.job_id, { mockup: true, awb: o.awb_included }])));
  const [busy, setBusy] = useState(false);
  const set = (id, k, v) => setSel(s => ({ ...s, [id]: { ...s[id], [k]: v } }));
  const save = async () => {
    if (busy) return;
    setBusy(true);
    const fails = [];
    for (const o of res.orders) {
      const j = byId[o.job_id];
      if (o.simulation || !sel[o.job_id].mockup || !j || j.mockup_printed_at) continue;
      try { await ensureProcessing(j); await axios.post(`${API}/shopee/${j.id}/mark-mockup-printed`, {}, opt); }
      catch (e) { fails.push(`${o.order_number}: ${errMsg(e, "gagal")}`); }
    }
    const awbIds = res.orders.filter(o => !o.simulation && o.awb_included && sel[o.job_id].awb).map(o => o.job_id);
    if (awbIds.length) {
      try { await axios.post(`${MS}/awb-printed`, { job_ids: awbIds }, opt); } catch (e) { fails.push(`Resi: ${errMsg(e, "gagal")}`); }
    }
    setBusy(false);
    if (fails.length) toast.error(`Sebagian gagal disimpan: ${fails.join("; ")}`); else toast.success("Hasil cetak tersimpan.");
    onClose(true);
  };
  return <div className="modal-backdrop" style={{ zIndex: 1200 }}>
    <div className="modal pcx" data-testid="mass-ship-print-confirm">
      <h3 style={{ margin: 0 }}>Apa yang benar-benar tercetak?</h3>
      <p className="small pcx-sum" data-testid="mass-ship-print-summary">{res.orders.length} pesanan · {res.pages.length} halaman</p>
      <p className="muted small pcx-hint">Hapus centang bila gagal tercetak — statusnya tidak berubah.</p>
      <div className="pcx-list">
        <div className="pcx-row pcx-head small muted"><span>Pesanan</span><span>Mockup</span><span>Resi</span></div>
        {res.orders.map(o => <div key={o.job_id} className="pcx-row" data-testid={`mass-ship-confirm-${o.job_id}`}>
          <span className="pcx-num"><b>{o.order_number}</b>{!o.awb_included && <small className="muted">TANPA RESI{o.awb_note ? ` — ${o.awb_note}` : ""}</small>}</span>
          <label className="pcx-cb"><input type="checkbox" checked={sel[o.job_id].mockup} onChange={e => set(o.job_id, "mockup", e.target.checked)}
            aria-label={`Mockup ${o.order_number} tercetak`} data-testid={`mass-ship-confirm-mockup-${o.job_id}`}/></label>
          <label className="pcx-cb" style={{ opacity: o.awb_included ? 1 : 0.4 }}><input type="checkbox" disabled={!o.awb_included} checked={!!sel[o.job_id].awb}
            onChange={e => set(o.job_id, "awb", e.target.checked)} aria-label={`Resi ${o.order_number} tercetak`} data-testid={`mass-ship-confirm-awb-${o.job_id}`}/></label>
        </div>)}
      </div>
      {res.skipped.length > 0 && <div className="warn-box small" data-testid="mass-ship-print-skipped">Tidak ikut PDF (status tidak berubah): {res.skipped.map(x => `${x.order_sn} (${x.reason})`).join(", ")}</div>}
      <details className="small pcx-more" data-testid="mass-ship-confirm-more">
        <summary>Opsi tambahan</summary>
        <div className="pcx-more-row">
          <a className="soft-btn" href={res.url} target="_blank" rel="noopener noreferrer" data-testid="mass-ship-open-pdf">Buka PDF</a>
          <a className="soft-btn" href={res.url} download={`mockup-resi-${Date.now()}.pdf`} data-testid="mass-ship-download-pdf">Unduh PDF</a>
        </div>
      </details>
      <div className="pcx-foot">
        <button type="button" className="primary pcx-save" disabled={busy} onClick={save} data-testid="mass-ship-confirm-save">{busy ? "Menyimpan…" : "Simpan Hasil Cetak"}</button>
        <button type="button" className="link-btn small" disabled={busy} onClick={() => onClose(false)} data-testid="mass-ship-confirm-cancel">Belum Dicetak — jangan ubah status</button>
      </div>
    </div>
  </div>;
}
